import type { User_role } from "@prisma/client";
import { ChangeFromGradeNumToString } from "src/feature/User/ChangeFromGradeNumToString";
import type {
  ChangeUserEmailInput,
  ChangeUserEmailResponse,
  ChangeUserGradeNumberInput,
  ResetUserPasswordInput,
  ResetUserPasswordResponse,
  SelectAdminUserData,
  SendLineMessageInput,
  UpdateUserLineIdInput,
  UpdateUserLineIdResponse,
  UserUpdateRequest,
} from "src/service/AdminApi/AdminUsers/types";
import { usersApi } from "src/service/UsersApi";
import type { GetUserData, GetUsersResponse } from "src/service/UsersApi/type";
import { serverApi } from "src/service/serverApi";

export const adminUsersApi = serverApi.injectEndpoints({
  endpoints: (builder) => {
    return {
      getSelectAdminUsers: builder.query<
        SelectAdminUserData,
        {
          cursor?: string;
          limit?: string;
          roles: User_role | string;
          keywords?: string;
          admin?: number;
          isIncludeDeleted?: boolean;
        }
      >({
        forceRefetch({ currentArg, previousArg }) {
          return currentArg?.cursor !== previousArg?.cursor;
        },
        merge: (currentCache, newItems, { arg }) => {
          if (arg.cursor === undefined || arg.cursor === "") {
            currentCache.data = newItems.data;
          } else {
            const existingIds = new Set(
              currentCache.data.map((item) => item.id),
            );
            const filteredNewItems = newItems.data.filter(
              (newItem) => !existingIds.has(newItem.id),
            );
            currentCache.data = currentCache.data.concat(filteredNewItems);
          }
          currentCache.pagination.endCursor = newItems.pagination.endCursor;
          currentCache.pagination.hasNextPage = newItems.pagination.hasNextPage;
        },
        query: (params) => {
          return {
            method: "get",
            params,
            url: `users`,
          };
        },
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
        transformResponse: (response: GetUsersResponse) => {
          const userData = response.data.map((user) => {
            let grade = "";
            if (user.student) {
              grade = ChangeFromGradeNumToString(user.student.gradeNum);
            }
            return {
              id: user.id,
              nickname: user.nickname,
              firstName: user.userSecret.firstName || "",
              lastName: user.userSecret.lastName || "",
              grade,
            };
          });
          const pagination = response.pagination;

          return {
            data: userData,
            pagination,
          };
        },
      }),
      patchUpdateGrade: builder.mutation<GetUserData, UserUpdateRequest>({
        invalidatesTags: ["UsersCoach"],
        query: (params) => {
          return {
            method: "PATCH",
            body: { grade: params.grade },
            url: `users/${params.id}`,
          };
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          try {
            const { data: editCoach } = await queryFulfilled;
            dispatch(
              usersApi.util.updateQueryData("getCoaches", {}, (draft) => {
                draft.data.map((coach) => {
                  if (coach.coach.id === editCoach.coach?.id) {
                    coach.coach.grade = editCoach.coach.grade;
                  }
                });
              }),
            );
          } catch {}
        },
      }),
      changeUserGradeNumber: builder.mutation<
        { success: boolean; message: string },
        ChangeUserGradeNumberInput
      >({
        invalidatesTags: ["UsersAdmin"],
        query: (params) => {
          return {
            method: "PATCH",
            body: params,
            url: `/users/change-grade-number`,
          };
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              usersApi.util.updateQueryData("getAdminUsers", {}, (draft) => {
                draft.data.map((user) => {
                  if (user.id === args.userId && !args.targetAllUser) {
                    user.student.gradeNum = args.toGradeNum ?? 0;
                  }
                });
              }),
            );
          } catch {}
        },
      }),
      reactiveAccountByAdmin: builder.mutation<
        {
          message: string;
          success: boolean;
        },
        { userId: string }
      >({
        invalidatesTags: ["UsersAll"],
        query: (params) => {
          return {
            method: "PATCH",
            url: `users/${params.userId}/reactive-account`,
          };
        },
      }),
      updateUserMemo: builder.mutation<
        {
          message: string;
          success: boolean;
        },
        { userId: string; memo: string }
      >({
        invalidatesTags: ["User", "UsersAll", "CoachSession"],
        query: (params) => {
          return {
            body: { memo: params.memo },
            method: "PATCH",
            url: `users/${params.userId}/memo`,
          };
        },
      }),
      cancelWithdrawal: builder.mutation<
        {
          message: string;
          success: boolean;
        },
        { userId: string }
      >({
        invalidatesTags: ["UsersAll"],
        query: (params) => {
          return {
            method: "DELETE",
            url: `users/${params.userId}/cancel`,
          };
        },
      }),
      sendLineMessages: builder.mutation<void, SendLineMessageInput>({
        query: (body) => {
          return {
            body,
            method: "POST",
            url: "users/send-line-messages",
          };
        },
      }),
      updateUserLineId: builder.mutation<
        UpdateUserLineIdResponse,
        UpdateUserLineIdInput
      >({
        invalidatesTags: ["User", "UsersAll"],
        query: (params) => {
          return {
            body: { lineId: params.lineId },
            method: "PATCH",
            url: `users/${params.userId}/line-id`,
          };
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              usersApi.util.updateQueryData("getAdminUsers", {}, (draft) => {
                draft.data.map((user) => {
                  if (user.id === args.userId) {
                    user.userSecret.lineId = args.lineId;
                  }
                });
              }),
            );
            dispatch(
              usersApi.util.updateQueryData("getCoaches", {}, (draft) => {
                draft.data.map((coach) => {
                  if (coach.id === args.userId) {
                    coach.userSecret.lineId = args.lineId;
                  }
                });
              }),
            );
          } catch (error) {
            console.error("Failed to update user LINE ID:", error);
          }
        },
      }),
      resetUserPassword: builder.mutation<
        ResetUserPasswordResponse,
        ResetUserPasswordInput
      >({
        query: (params) => {
          return {
            body: {
              newPassword: params.newPassword,
              confirmPassword: params.confirmPassword,
            },
            method: "PATCH",
            url: `users/${params.userId}/reset-password`,
          };
        },
      }),
      changeUserEmail: builder.mutation<
        ChangeUserEmailResponse,
        ChangeUserEmailInput
      >({
        invalidatesTags: ["User", "UsersAll"],
        query: (params) => {
          return {
            body: {
              newEmail: params.newEmail,
              confirmEmail: params.confirmEmail,
            },
            method: "PATCH",
            url: `users/${params.userId}/change-email`,
          };
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;

            // Update cache with new email
            dispatch(
              usersApi.util.updateQueryData("getAdminUsers", {}, (draft) => {
                draft.data.forEach((user) => {
                  if (user.id === args.userId) {
                    user.userSecret.email = args.newEmail;
                  }
                });
              }),
            );

            dispatch(
              usersApi.util.updateQueryData("getCoaches", {}, (draft) => {
                draft.data.forEach((coach) => {
                  if (coach.id === args.userId) {
                    coach.coach.preCoach.email = args.newEmail;
                  }
                });
              }),
            );
          } catch (error) {
            console.error("Failed to update user email:", error);
          }
        },
      }),
    };
  },
  overrideExisting: true,
});

export const {
  useGetSelectAdminUsersQuery,
  usePatchUpdateGradeMutation,
  useChangeUserGradeNumberMutation,
  useReactiveAccountByAdminMutation,
  useUpdateUserMemoMutation,
  useCancelWithdrawalMutation,
  useSendLineMessagesMutation,
  useUpdateUserLineIdMutation,
  useResetUserPasswordMutation,
  useChangeUserEmailMutation,
} = adminUsersApi;
